import { InvalidFileError, MigrationError, MigrationNeededError } from '@/common/errors';
import { Project, ProjectJSON } from '@/types/helpers';
import { ImportReturnType } from '@/types/stores/rootActions';

type MigrationFunction = (data: Project) => Project | Promise<Project>;
export type MigrationReturn = ImportReturnType | { kind: 'project'; project: ProjectJSON };

export async function* migrateImportedJson(
  jsonData: string,
  currentSchemaPath = '',
  doMigrate = false,
  force = false,
): AsyncGenerator<MigrationReturn, MigrationReturn, MigrationReturn> {
  const maybeData = JSON.parse(jsonData);
  if (maybeData?.generator !== 'Halton AirChef') {
    throw new InvalidFileError('notValid');
  }
  const data = maybeData as ProjectJSON;

  const dataSchemaVersion = extractVersion(data.project.schemaPath);
  // const targetSchemaVersion = {
  //   versionNumber: dataSchemaVersion.versionNumber + 3,
  //   versionString: `v${dataSchemaVersion.versionNumber + 3}`,
  // };
  const targetSchemaVersion = extractVersion(currentSchemaPath || data.project.schemaPath);

  let failures = false;

  if (targetSchemaVersion.versionNumber < dataSchemaVersion.versionNumber) {
    console.warn('Target schema version is older than loaded schema version!');
  } else if (targetSchemaVersion.versionNumber > dataSchemaVersion.versionNumber) {
    if (force) {
      return {
        kind: 'project',
        project: data,
      };
    }
    if (!doMigrate) {
      throw new MigrationNeededError('Migration needed', jsonData);
    }
    const migrations: [string, string][] = [];
    for (let v = dataSchemaVersion.versionNumber; v < targetSchemaVersion.versionNumber; v++) {
      migrations.push([`v${v}`, `v${v + 1}`]);
    }

    yield {
      kind: 'todo',
      migrations: migrations.map(([source, target]) => ({
        id: `${source}${target}`,
        source,
        target,
        result: 'PENDING',
      })),
    };

    console.log('Starting schema migrations', migrations);

    for (const [source, target] of migrations) {
      let migration: MigrationFunction | null = null;
      console.log(`Migrating ${source} to ${target}...`);
      try {
        migration = (await import(`./migrations/${source}-to-${target}.ts`)).default as MigrationFunction;
        if (!migration) {
          throw new MigrationError(`No migration function found in imported file from ${source} to  ${target}`);
        }
      } catch (err) {
        failures = true;
        console.warn(`No migration available for ${source} to ${target}`, err);
        // By erroring here, we require explicit migration function even if no changes are needed.
        yield {
          kind: 'done',
          migration: {
            id: `${source}${target}`,
            result: 'FAIL',
            source,
            target,
            error:
              err instanceof MigrationError
                ? err
                : new MigrationError(`No migration available from ${source} to ${target}`),
          },
        };
      }

      if (!migration) continue;

      try {
        data.project = await migration(data.project);
        yield {
          kind: 'done',
          migration: {
            id: `${source}${target}`,
            result: 'OK',
            source,
            target,
          },
        };
      } catch (err) {
        console.error(`Migration from ${source} to ${target} failed!`, err);
        failures = true;
        yield {
          kind: 'done',
          migration: {
            id: `${source}${target}`,
            result: 'FAIL',
            source,
            target,
            error: err instanceof Error ? err : new MigrationError('Unknown error', { cause: err }),
          },
        };
      }
    }

    console.log('Schema migrations done');
  }

  if (failures) {
    throw new InvalidFileError('schemaMigrationFailed', false);
  }

  return {
    kind: 'project',
    project: data,
  };
}

// "/schemas/v1/project/project.schema.json"
function extractVersion(schemaPath: string) {
  if (typeof schemaPath !== 'string' || !schemaPath.startsWith('/schemas/')) {
    throw new InvalidFileError('invalidSchema');
  }
  const [, , versionString] = schemaPath.split('/');
  const versionNumber = Number.parseInt(versionString.replace('v', ''), 10);

  return {
    versionNumber,
    versionString,
  };
}
